package br.com.vvrefeicoes.portal.feature.shell

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandHorizontally
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkHorizontally
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationDrawerItem
import androidx.compose.material3.NavigationDrawerItemDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavDestination
import androidx.navigation.NavDestination.Companion.hasRoute
import androidx.navigation.NavDestination.Companion.hierarchy
import androidx.navigation.NavGraph.Companion.findStartDestination
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppLogoWhite
import br.com.vvrefeicoes.portal.core.ui.components.LocalOpenPrivacyPolicy
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.companyRefs
import br.com.vvrefeicoes.portal.feature.notifications.LocalNotifications
import br.com.vvrefeicoes.portal.feature.notifications.NotificationsOverlays
import br.com.vvrefeicoes.portal.feature.notifications.rememberNotificationsController
import br.com.vvrefeicoes.portal.navigation.AdminNavHost
import br.com.vvrefeicoes.portal.navigation.GuideCreateRoute
import br.com.vvrefeicoes.portal.navigation.GuideDetailRoute
import br.com.vvrefeicoes.portal.navigation.GuideEditRoute
import br.com.vvrefeicoes.portal.navigation.GuidesAndManualsRoute
import br.com.vvrefeicoes.portal.navigation.HomeRoute
import br.com.vvrefeicoes.portal.navigation.NotificationsRoute
import br.com.vvrefeicoes.portal.navigation.PrivacyPolicyRoute
import br.com.vvrefeicoes.portal.navigation.ProfileRoute
import br.com.vvrefeicoes.portal.navigation.SyncNavigationWithUrl
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronUp
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.PanelLeftClose
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

private val SidebarWidth = 280.dp
private const val SIDEBAR_ANIMATION_MILLIS = 260

@Composable
fun AdminShell() {
    val appGraph = LocalAppGraph.current
    val navController = rememberNavController()
    SyncNavigationWithUrl(navController)
    val scope = rememberCoroutineScope()
    val filters = rememberGlobalFilters()
    val period = rememberGlobalPeriod()
    val notifications = rememberNotificationsController()
    var sidebarExpanded by rememberSaveable { mutableStateOf(true) }
    var expandedMenus by remember { mutableStateOf(emptySet<String>()) }
    val session by appGraph.authRepository.session.collectAsStateWithLifecycle()

    val permissions = session?.permissions.orEmpty()
    val hasAllUnits = PermissionKeys.SEE_ALL_UNITS in permissions
    val hasLinkedUnits = PermissionKeys.SEE_LINKED_UNITS in permissions
    val showUnits = hasAllUnits || hasLinkedUnits
    val showManagers = PermissionKeys.SEE_MANAGERS in permissions
    val showDirectors = PermissionKeys.SEE_DIRECTORS in permissions

    LaunchedEffect(appGraph, session) {
        // O organograma alimenta os filtros (diretoras/gestoras/unidades). Usuários com escopo
        // (unidades vinculadas ou todas) também podem lê-lo, mesmo sem a permissão de organograma.
        val data =
            if (PermissionKeys.ORG_CHART in permissions || hasAllUnits || hasLinkedUnits) {
                runCatching {
                    appGraph.orgChartRepository
                        .loadInitial()
                        .currentVersion.data
                }.getOrNull()
            } else {
                null
            }
        val units =
            when {
                hasAllUnits -> data?.companyRefs().orEmpty()
                hasLinkedUnits -> session?.companies.orEmpty()
                else -> emptyList()
            }
        filters.setSource(data, units)
    }

    LaunchedEffect(notifications) {
        notifications.init()
    }

    val visibleItems = shellMenuItems().visibleFor(session?.permissions?.keys.orEmpty())
    val currentDestination = navController.currentBackStackEntryAsState().value?.destination
    val currentItem = findCurrentItem(visibleItems, currentDestination)

    LaunchedEffect(currentItem) {
        expandedMenus = expandedMenus + findParentLabels(visibleItems, currentItem)
    }

    fun navigate(route: Any) {
        navController.navigate(route) {
            popUpTo(navController.graph.findStartDestination().id) { saveState = true }
            launchSingleTop = true
        }
    }

    Row(modifier = Modifier.fillMaxSize()) {
        AnimatedVisibility(
            visible = sidebarExpanded,
            enter =
                expandHorizontally(
                    animationSpec = tween(durationMillis = SIDEBAR_ANIMATION_MILLIS, easing = FastOutSlowInEasing),
                    expandFrom = Alignment.Start,
                ) + fadeIn(tween(durationMillis = SIDEBAR_ANIMATION_MILLIS)),
            exit =
                shrinkHorizontally(
                    animationSpec = tween(durationMillis = SIDEBAR_ANIMATION_MILLIS, easing = FastOutSlowInEasing),
                    shrinkTowards = Alignment.Start,
                ) + fadeOut(tween(durationMillis = SIDEBAR_ANIMATION_MILLIS)),
        ) {
            AdminSidebar(
                items = visibleItems,
                expandedMenus = expandedMenus,
                onToggleMenu = { label ->
                    expandedMenus =
                        if (label in expandedMenus) expandedMenus - label else expandedMenus + label
                },
                currentDestination = currentDestination,
                onNavigate = ::navigate,
                onCollapse = { sidebarExpanded = false },
            )
        }

        CompositionLocalProvider(
            LocalGlobalFilters provides filters,
            LocalGlobalPeriod provides period,
            LocalOpenPrivacyPolicy provides { navigate(PrivacyPolicyRoute) },
            LocalNotifications provides notifications,
        ) {
            Scaffold(
                topBar = {
                    AdminTopBar(
                        sidebarExpanded = sidebarExpanded,
                        onExpandSidebar = { sidebarExpanded = true },
                        onHome = { navigate(HomeRoute) },
                        filters = filters,
                        units =
                            if (filters.units.size == 1) {
                                filters.units.map { it.name }
                            } else {
                                listOf(ALL_UNITS) + filters.units.map { it.name }
                            },
                        managers = listOf(ALL_MANAGERS) + filters.managers,
                        directors = listOf(ALL_DIRECTORS) + filters.directors,
                        showUnits = showUnits,
                        showManagers = showManagers,
                        showDirectors = showDirectors,
                        userName = session?.userName.orEmpty(),
                        onGoToNotifications = { navigate(NotificationsRoute) },
                        onGoToProfile = { navigate(ProfileRoute) },
                        onLogout = { scope.launch { appGraph.authRepository.logout() } },
                    )
                },
            ) { innerPadding ->
                Column(
                    modifier =
                        Modifier
                            .fillMaxSize()
                            .padding(innerPadding),
                ) {
                    appGraph.loadError.current?.let { resource ->
                        AppBanner(
                            text = stringResource(resource),
                            isError = true,
                            onDismiss = appGraph.loadError::dismiss,
                            modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space8),
                        )
                    }
                    NotificationsOverlays(onOpenList = { navigate(NotificationsRoute) })
                    Box(modifier = Modifier.weight(1f)) {
                        AdminNavHost(navController = navController, onNavigate = ::navigate)
                    }
                }
            }
        }
    }
}

@Composable
private fun AdminSidebar(
    items: List<ShellMenuItem>,
    expandedMenus: Set<String>,
    onToggleMenu: (String) -> Unit,
    currentDestination: NavDestination?,
    onNavigate: (Any) -> Unit,
    onCollapse: () -> Unit,
) {
    val itemColors =
        NavigationDrawerItemDefaults.colors(
            selectedContainerColor = MaterialTheme.colorScheme.primaryContainer,
            selectedIconColor = MaterialTheme.colorScheme.onPrimaryContainer,
            selectedTextColor = MaterialTheme.colorScheme.onPrimaryContainer,
            unselectedIconColor = MaterialTheme.colorScheme.onPrimary,
            unselectedTextColor = MaterialTheme.colorScheme.onPrimary,
        )

    Column(
        modifier =
            Modifier
                .fillMaxHeight()
                .width(SidebarWidth)
                .background(MaterialTheme.colorScheme.primary)
                .verticalScroll(rememberScrollState())
                .padding(vertical = AppSpacing.space16),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
    ) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = AppSpacing.space16),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            AppLogoWhite(
                modifier =
                    Modifier
                        .height(32.dp)
                        .clickable { onNavigate(HomeRoute) },
                contentDescription = "Ir para o início",
            )
            IconButton(onClick = onCollapse) {
                Icon(
                    imageVector = Lucide.PanelLeftClose,
                    contentDescription = "Recolher menu",
                    tint = MaterialTheme.colorScheme.onPrimary,
                )
            }
        }
        Spacer(Modifier.height(AppSpacing.space8))

        items.forEach { item ->
            SidebarMenuItem(
                item = item,
                depth = 0,
                itemColors = itemColors,
                expandedMenus = expandedMenus,
                onToggleMenu = onToggleMenu,
                currentDestination = currentDestination,
                onNavigate = onNavigate,
            )
        }
    }
}

@Composable
private fun SidebarMenuItem(
    item: ShellMenuItem,
    depth: Int,
    itemColors: androidx.compose.material3.NavigationDrawerItemColors,
    expandedMenus: Set<String>,
    onToggleMenu: (String) -> Unit,
    currentDestination: NavDestination?,
    onNavigate: (Any) -> Unit,
) {
    if (item.children.isEmpty()) {
        NavigationDrawerItem(
            label = { Text(item.label) },
            icon = menuItemIcon(item.icon),
            selected = isSelected(item, currentDestination),
            onClick = { item.route?.let(onNavigate) },
            colors = itemColors,
            modifier =
                Modifier
                    .padding(horizontal = AppSpacing.space8)
                    .padding(start = (AppSpacing.space24.value * depth).dp),
        )
        return
    }

    val expanded = item.label in expandedMenus
    NavigationDrawerItem(
        label = { Text(item.label) },
        icon = menuItemIcon(item.icon),
        selected = false,
        onClick = { onToggleMenu(item.label) },
        badge = {
            Icon(
                imageVector = if (expanded) Lucide.ChevronUp else Lucide.ChevronDown,
                contentDescription = null,
            )
        },
        colors = itemColors,
        modifier =
            Modifier
                .padding(horizontal = AppSpacing.space8)
                .padding(start = (AppSpacing.space24.value * depth).dp),
    )
    AnimatedVisibility(
        visible = expanded,
        enter = expandVertically(),
        exit = shrinkVertically(),
    ) {
        Column {
            item.children.forEach { child ->
                SidebarMenuItem(
                    item = child,
                    depth = depth + 1,
                    itemColors = itemColors,
                    expandedMenus = expandedMenus,
                    onToggleMenu = onToggleMenu,
                    currentDestination = currentDestination,
                    onNavigate = onNavigate,
                )
            }
        }
    }
}

private fun menuItemIcon(icon: ImageVector?): (@Composable () -> Unit)? {
    if (icon == null) return null
    return { Icon(imageVector = icon, contentDescription = null) }
}

private fun isSelected(
    item: ShellMenuItem,
    destination: NavDestination?,
): Boolean {
    val route = item.route ?: return false
    return destination?.hierarchy?.any { node ->
        node.hasRoute(route::class) ||
            (
                route is GuidesAndManualsRoute &&
                    (
                        node.hasRoute(GuideDetailRoute::class) ||
                            node.hasRoute(GuideCreateRoute::class) ||
                            node.hasRoute(GuideEditRoute::class)
                    )
            )
    } == true
}

private fun findCurrentItem(
    items: List<ShellMenuItem>,
    destination: NavDestination?,
): ShellMenuItem? {
    items.forEach { item ->
        if (isSelected(item, destination)) return item
        findCurrentItem(item.children, destination)?.let { return it }
    }
    return null
}

private fun findParentLabels(
    items: List<ShellMenuItem>,
    target: ShellMenuItem?,
): Set<String> {
    if (target == null) return emptySet()
    items.forEach { item ->
        if (item.children.any { it == target }) {
            return setOf(item.label) + findParentLabels(item.children, target)
        }
        val nestedParents = findParentLabels(item.children, target)
        if (nestedParents.isNotEmpty()) {
            return setOf(item.label) + nestedParents
        }
    }
    return emptySet()
}
